import {
  Box,
  Grid2 as Grid,
  IconButton,
  styled,
  Typography
} from '@mui/material'
import ButtonBase from 'design-system/button'
import NavLinkBase from 'design-system/NavLink'
import { ADD_MEDICAL_EVENT, EDIT_MEDICAL_EVENT } from 'navigation/CONSTANTS'
import React, { useState } from 'react'
import { useList } from '../../hooks'
import { GridColDef } from '@mui/x-data-grid'
import { IMedicalEventResponse } from 'types'
import AddIcon from '@mui/icons-material/Add'
import { Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material'
import {
  MEDICAL_EVENT_REGISTRATION,
  MEDICAL_EVENT_REGISTRATION_FILTERED
} from 'services/medical_event/CONSTANTS'
import ConfirmationModal from 'components/ConfirmationModal'
import { useToast } from 'services/context/toast'
import DataGridBase from 'design-system/DataGrid'

const StyledDataGrid = styled(DataGridBase<IMedicalEventResponse>)(
  ({ theme }) => ({
    '& .MuiDataGrid-topContainer': {
      '& [role="row"]': {
        backgroundColor: theme.palette.primarySoft.main
      },
      '&, & svg': {
        color: theme.palette.primarySoft.contrastText
      }
    }
  })
)

const columnsGuide: GridColDef<IMedicalEventResponse>[] = [
  {
    field: 'event_name',
    headerName: 'Nome do Evento',
    sortable: true,
    flex: 3
  },
  {
    field: 'description',
    headerName: 'Descrição',
    sortable: true,
    flex: 5
  },
  {
    field: 'created_at',
    headerName: 'Data de Criação',
    sortable: true,
    flex: 2,
    valueFormatter: params => {
      if (!params) return ''
      const date = new Date(params)
      return date.toLocaleString()
    }
  },
  {
    field: 'updated_at',
    headerName: 'Última Atualização',
    sortable: true,
    flex: 2,
    valueFormatter: params => {
      if (!params) return ''
      const date = new Date(params)
      return date.toLocaleString()
    }
  }
]

const MedicalEventsPage: React.FC = () => {
  const { handleEdit, list, state, handleDelete } =
    useList<IMedicalEventResponse>({
      api: MEDICAL_EVENT_REGISTRATION,
      editPage: item => EDIT_MEDICAL_EVENT(item.toString()),
      deleteApi: (id: string) => MEDICAL_EVENT_REGISTRATION_FILTERED(id)
    })

  const [deleteItem, setDeleteItem] = useState<IMedicalEventResponse>()

  const toast = useToast()

  const handleDeleteConfirmation = async () => {
    if (deleteItem) {
      const result = await handleDelete(deleteItem.medical_event_id)

      if (result) {
        toast.handler({
          content: 'Evento médico excluído com sucesso',
          severity: 'success'
        })
      } else {
        toast.handler({
          content: 'Erro ao excluir evento médico',
          severity: 'error'
        })
      }
      setDeleteItem(undefined)
    } else {
      console.error('No item selected for deletion')
    }
  }

  const columns: GridColDef<IMedicalEventResponse>[] = [
    ...columnsGuide,
    {
      field: 'settings',
      headerName: 'Ações',
      sortable: false,
      filterable: false,
      renderCell: params => (
        <>
          <IconButton onClick={() => handleEdit(params.id, params.row)}>
            <EditIcon />
          </IconButton>
          <IconButton
            disabled={!!deleteItem}
            onClick={() => setDeleteItem(params.row)}
          >
            <DeleteIcon />
          </IconButton>
        </>
      ),
      width: 100
    }
  ]

  return (
    <Box display={'flex'} sx={{ justifyContent: 'center' }}>
      <Grid container spacing={2} flex={1} maxWidth={'1200px'} p={2}>
        <Grid
          container
          size={12}
          spacing={1}
          justifyContent='space-between'
          alignItems={'center'}
        >
          <Typography variant='h5'>Eventos Médicos</Typography>
          <NavLinkBase to={ADD_MEDICAL_EVENT} absolute>
            <ButtonBase
              sx={{
                '& .MuiButton-icon': {
                  marginLeft: '0px'
                }
              }}
              endIcon={<AddIcon />}
            >
              <Typography variant='body1'>Adicionar evento médico</Typography>
            </ButtonBase>
          </NavLinkBase>
        </Grid>
        <Grid flex={1}>
          <StyledDataGrid
            rows={list}
            columns={columns}
            getRowId={row => row.medical_event_id}
            loading={state == 'loading'}
            disableRowSelectionOnClick
            disableColumnMenu
            pageSizeOptions={[25, 50, 100]}
            slotProps={{
              pagination: {
                labelDisplayedRows: ({ from, to, count }) =>
                  `Exibindo ${from}–${to} de ${count} registros`
              },
              noRowsOverlay: {
                'aria-label': 'Nenhum estabelecimento encontrado'
              }
            }}
          />
        </Grid>
      </Grid>
      <ConfirmationModal
        open={deleteItem != null}
        title='Essa ação removerá o evento para o paciente, porém não afetará os dados já cadastrados. Tem certeza que deseja excluir?'
        onConfirm={handleDeleteConfirmation}
        onCancel={() => setDeleteItem(undefined)}
        actionContainer={{ justifyContent: 'center', paddingTop: '16px' }}
      />
    </Box>
  )
}

export default MedicalEventsPage
